import type { AgentsBoard, Proposal, ProposalKind } from '../../api/agentSchemas';
import { spoken, type AgentLine } from './agentLine';
import type { LineId } from './voice/lines';

/** What the team says about the work it prepared: what is waiting, what comes first, how to react to it. */

type DayPart = 'manana' | 'tarde' | 'noche';

export function pendingOf(board: AgentsBoard): Proposal[] {
  return board.proposals.filter((proposal) => proposal.status === 'pending');
}

/** Júpiter opens: which part of the day it is and whether something waits for review. */
export function greeting(board: AgentsBoard, now: Date, timeZone?: string): AgentLine {
  const part = dayPart(now, timeZone);
  const pending = pendingOf(board);
  const first = pending[0];
  if (!first) return spoken('direccion', `saludo-${part}-aldia`);
  const count = pending.length === 1 ? '1 cosa' : `${pending.length} cosas`;
  return spoken('direccion', `saludo-${part}-pendientes`, {
    note: `${count} para revisar · ${first.title}`,
    proposalId: first.id,
  });
}

const NOTHING: Readonly<Record<ProposalKind, AgentLine>> = {
  win_back: spoken('marketing', 'winback-nada'),
  confirm_visits: spoken('operaciones', 'confirm-nada'),
  offer: spoken('marketing', 'offer-nada'),
};

const HANDOFF: Readonly<Record<ProposalKind, LineId>> = {
  win_back: 'winback-handoff',
  confirm_visits: 'confirm-handoff',
  offer: 'offer-handoff',
};

/** The pending proposal of this kind, handed over by the agent that prepared it, or an honest "nothing". */
export function aboutKind(board: AgentsBoard, kind: ProposalKind): AgentLine {
  const proposal = pendingOf(board).find((candidate) => candidate.kind === kind);
  if (!proposal) return NOTHING[kind];
  return spoken(proposal.agentId, HANDOFF[kind], { note: proposal.reason, proposalId: proposal.id });
}

export function firstThing(board: AgentsBoard): AgentLine {
  const first = pendingOf(board)[0];
  if (!first) return spoken('direccion', 'primero-aldia');
  return spoken('direccion', 'primero-pendiente', { note: `${first.title}. ${first.reason}`, proposalId: first.id });
}

/**
 * The owner reacts to the work (refuses it, approves it, wants it changed): the agent that owns the proposal answers,
 * with the review button on, since nothing is decided by talking. It is the pending proposal of the kind the sentence
 * names ("esa oferta no me convence"), and the first one still waiting when it names none.
 */
export function aboutProposal(board: AgentsBoard, voz: LineId, named?: ProposalKind | null): AgentLine {
  const waiting = pendingOf(board);
  const proposal = waiting.find((candidate) => candidate.kind === named) ?? waiting[0];
  if (!proposal) return spoken('direccion', 'propuesta-nada');
  const note = voz === 'propuesta-porque' ? proposal.reason : proposal.title;
  return spoken(proposal.agentId, voz, { note, proposalId: proposal.id });
}

/** Wants more clients: Venus shows what she has ready (an offer first, else the win-back), or says there is nothing yet. */
export function moreClients(board: AgentsBoard): AgentLine {
  const waiting = pendingOf(board);
  const proposal = waiting.find((candidate) => candidate.kind === 'offer') ?? waiting.find((candidate) => candidate.kind === 'win_back');
  if (!proposal) return spoken('marketing', 'mas-clientes-nada');
  return spoken('marketing', 'mas-clientes-hay', { note: proposal.title, proposalId: proposal.id });
}

function dayPart(now: Date, timeZone?: string): DayPart {
  const hour = Number(new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23', timeZone }).format(now));
  if (hour < 12) return 'manana';
  if (hour < 19) return 'tarde';
  return 'noche';
}
